import React, { ReactElement } from 'react';

import last from 'lodash/last';

import {
    Block,
    Blocks,
    isArrayContainerBlock,
    isResourceContainerBlock,
    isSingleContainerBlock,
    Widget,
} from '../../types';
import { BLOCK_RENDERERS } from './blocks';

/**
 * Recursively render blocks.
 */
export function renderBlock(path: Required<Block>['path'], block: Blocks, widget: Widget) {
    const Renderer: React.FC<any> | undefined = block.type && (BLOCK_RENDERERS as any)[block.type];
    const { theme } = block;
    if (!Renderer) {
        return null;
    }
    Renderer.displayName = block.type;
    const lastPath = last(path);

    if (isArrayContainerBlock(block)) {
        const { items, ...container } = block;
        return (
            <Renderer key={lastPath} path={path} {...container}>
                {(items as Blocks[]).map((item, index) =>
                    renderBlock([...path, 'items', index.toString()], { ...item, theme }, widget),
                )}
            </Renderer>
        );
    }

    if (isSingleContainerBlock(block)) {
        return (
            <Renderer key={lastPath} path={path} widget={widget} legacyWidget={widget.legacyWidget} {...block}>
                {renderBlock([...path, 'container'], { ...block.container, theme }, widget)}
            </Renderer>
        );
    }

    if (isResourceContainerBlock(block)) {
        const { resourceType, ...container } = block;
        const resourceBlock = container[resourceType];
        // If the resource container does not have the matching resource block, don't render the block.
        if (!resourceBlock) {
            return null;
        }
        return (
            <Renderer key={lastPath} path={path} {...block}>
                {renderBlock([...path, 'resource'], { ...resourceBlock, theme }, widget)}
            </Renderer>
        );
    }

    return <Renderer key={lastPath} path={path} {...block} widget={widget} />;
}

/**
 * Render widget content block tree.
 */
export function renderBlockTree(widget: Widget): ReactElement | null {
    const { widgetId, body } = widget;
    const { theme } = body?.style || {};

    if (!widgetId || !body) {
        return null;
    }

    return renderBlock([widgetId, 'body'], { ...body, theme }, widget);
}
